import moment from 'moment';
import React, { useEffect, useMemo } from 'react';
import { Navigate, useSearchParams } from 'react-router';
import { useQuery } from '@apollo/client';
import Page from '../app/layout/Page';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import Button from '../components/Button';
import { AssetTokenProvider } from '../editor/AssetTokenContext';
import QuillContext from '../editor/QuillContext';
import { gql } from '../graphql/generated/gql';
import InternalTopbar from '../topbar/InternalTopbar';
import { UIActions } from '../ui/uiSlice';
import { isCommentOrPublicCommentThread } from '../utils/post/threadUtils';
import useNavigate from '../utils/router/useNavigate';
import useSlug from '../utils/router/useSlug';
import postPath from '../utils/url/postPath';
import VerifyPostModal from './VerifyPostModal';
import {
  BasePostHistoryView,
  PostDoneButton,
  useCalculatedVersionChanges,
  useMagneticWallVersionChanges,
} from './history/PostHistoryView';
import ResponsiveDate from './history/ResponsiveDate';
import VersionChanges from './history/VersionChanges';
import isVerifier from './isVerifier';
import usePostVerifyNavigation from './usePostVerifyNavigation';

const POST_VERIFY_HISTORY_RANGE_QUERY = gql(/** @complexity 53 */ `
  query PostVerifyHistoryRange($postId: ID!, $versionFrom: Int!, $versionTo: Int!) {
    post(id: $postId) {
      id
      editedAt
      verificationEntries {
        id
        verifiedAt
        version
      }
      content(isLatest: true){
        id
        version
      }
      historyRange(versionFrom: $versionFrom, versionTo: $versionTo) {
        id
        diff
        snapshot
        annotations {
          id
          ...BasePostHistoryView_annotation
        }
        threads {
          id
          ...BasePostHistoryView_thread
        }
      }
      ...VerifyPostModal_post
      ...BasePostHistoryView_post
      ...postPath_post
      ...isVerifier_post
    }
    entityAssetToken(entityId: $postId, entityType: POST) {
      ...AssetTokenProvider_entityAssetToken
    }
  }
`);

interface PostVerifyViewInnerProps {
  versionFrom: number;
  versionTo: number;
  postId: string;
}

const PostVerifyViewInner = ({
  versionFrom,
  versionTo,
  postId,
}: PostVerifyViewInnerProps) => {
  const [, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.session.user);

  const { data, error, loading } = useQuery(POST_VERIFY_HISTORY_RANGE_QUERY, {
    fetchPolicy: 'network-only',
    variables: {
      postId,
      versionFrom,
      versionTo,
    },
  });

  const { post, entityAssetToken } = data ?? {};

  const { content, editVersionChanges } = useCalculatedVersionChanges({
    diff: post?.historyRange.diff,
    snapshot: post?.historyRange.snapshot,
  });

  const verificationEntry = post?.verificationEntries.at(0);
  const verificationEntryVersion = verificationEntry?.version;
  const currentPostVersion = post?.content.version;

  const threads = useMemo(
    () => post?.historyRange.threads.filter(isCommentOrPublicCommentThread),
    [post?.historyRange.threads],
  );

  const magneticWallVersionChanges = useMagneticWallVersionChanges({
    threads,
    annotations: post?.historyRange.annotations,
    compareDate: moment(verificationEntry?.verifiedAt),
    content,
  });

  useEffect(() => {
    if (
      verificationEntryVersion != null &&
      currentPostVersion != null &&
      (versionFrom !== verificationEntryVersion ||
        versionTo !== currentPostVersion)
    ) {
      setSearchParams({
        histories: `${verificationEntryVersion}-${currentPostVersion}`,
      });
    }
  }, [
    versionFrom,
    versionTo,
    verificationEntryVersion,
    currentPostVersion,
    setSearchParams,
  ]);

  const hasChanges =
    magneticWallVersionChanges.filter((change) => {
      if (change.type === 'comment') {
        return change.newCommentIds.size > 0;
      }
      return change.isNew;
    }).length > 0 ||
    (editVersionChanges && editVersionChanges.length > 0);

  // If there are no changes, we redirect to the post path with the
  // verification state to show the simple reverify modal in PostView instead
  if (post && !loading && !hasChanges) {
    return (
      <Navigate to={postPath(post)} replace state={{ verification: true }} />
    );
  }

  if (
    post &&
    !loading &&
    (!verificationEntry || !isVerifier(post, currentUser))
  ) {
    return <Navigate to={postPath(post)} replace />;
  }

  if (error) {
    // The error might happen because the history is not found or the post is not found.
    // If the history is not found, we attempt to redirect to /verify to get the last history.
    // If another NOT_FOUND error happens, this will be handled by usePostVerifyNavigation.
    if (error.message === 'NOT_FOUND') {
      return (
        <Navigate
          to={`${postPath({ id: postId, title: '' })}/verify`}
          replace
        />
      );
    }

    // If any other error happens, we redirect to the post path to let it handle the error.
    return <Navigate to={postPath({ id: postId, title: '' })} replace />;
  }

  if (!post || !entityAssetToken) return null;

  return (
    <AssetTokenProvider entityAssetToken={entityAssetToken}>
      <Page
        topbar={
          <InternalTopbar
            postActions={
              <>
                <Button
                  onClick={() =>
                    dispatch(
                      UIActions.openModal(VerifyPostModal, {
                        post,
                        onVerify: () => {
                          navigate(postPath(post));
                        },
                      }),
                    )
                  }
                  variant="primary"
                >
                  Reverify
                </Button>
                <PostDoneButton postId={postId} />
              </>
            }
          />
        }
      >
        <BasePostHistoryView
          magneticWallVersionChanges={magneticWallVersionChanges}
          post={post}
          editedAt={post.editedAt}
          editVersionChanges={editVersionChanges}
          content={content}
          topbar={
            <VersionChanges.Topbar>
              <VersionChanges.Meta>
                {verificationEntry && (
                  <ResponsiveDate
                    date={verificationEntry.verifiedAt}
                    render={({ condensed, dateWithPreposition }) => {
                      return condensed
                        ? `Verified ${dateWithPreposition}`
                        : `Post last verified ${dateWithPreposition}`;
                    }}
                  />
                )}
              </VersionChanges.Meta>
            </VersionChanges.Topbar>
          }
        />
      </Page>
    </AssetTokenProvider>
  );
};

const PostVerifyView = () => {
  const [postId] = useSlug('postSlug');

  const [searchParams] = useSearchParams();

  const historiesParam = searchParams.get('histories');
  const [, from, to] = historiesParam?.match(/(\d+)-(\d+)/) ?? [];

  const { postVerifyNavigate } = usePostVerifyNavigation(postId);

  const shouldRedirect = from == null || to == null || from === to;

  useEffect(() => {
    if (shouldRedirect) {
      postVerifyNavigate({ replace: true });
    }
  }, [postVerifyNavigate, shouldRedirect]);

  if (shouldRedirect) {
    return null;
  }

  return (
    <QuillContext>
      <PostVerifyViewInner
        versionFrom={parseInt(from, 10)}
        versionTo={parseInt(to, 10)}
        postId={postId}
      />
    </QuillContext>
  );
};

export default PostVerifyView;
